import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  Alert,
  Box, 
  Button, 
  TextField, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select, 
  Paper, 
  Typography,
  CircularProgress,
  FormHelperText,
  SelectChangeEvent
} from '@mui/material';
import { usersApi } from '../../services/api';
import { User, UserRole, ROLES } from '../../types';

interface UserFormData {
  name: string;
  email: string;
  role: UserRole;
  password: string;
  confirmPassword: string;
}

interface UserFormErrors {
  name?: string;
  email?: string;
  role?: string;
  password?: string;
  confirmPassword?: string;
  submit?: string;
}

export default function UserForm() {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<UserFormData>({
    name: '',
    email: '',
    role: ROLES.CLIENT,
    password: '',
    confirmPassword: ''
  });
  
  // Get role options with translated labels
  const roleOptions = [
    { value: ROLES.ADMIN, label: 'admin' },
    { value: ROLES.DRIVER, label: 'driver' },
    { value: ROLES.CLIENT, label: 'client' }
  ];
  
  const [errors, setErrors] = useState<UserFormErrors>({});
  const { t } = useLanguage();

  useEffect(() => {
    if (isEdit && id) {
      const loadUser = async () => {
        try {
          setLoading(true);
          const user = await usersApi.getById(parseInt(id));
          setFormData({
            name: user.name,
            email: user.email,
            role: user.role,
            password: '',
            confirmPassword: ''
          });
        } catch (err) {
          setError(t('errors.loadUser'));
          console.error('Error loading user:', err);
        } finally {
          setLoading(false);
        }
      };
      loadUser();
    }
  }, [id, isEdit]);

  // Check if password is required based on role
  const isPasswordRequired = (): boolean => {
    // For client users, password is never required
    if (formData.role === ROLES.CLIENT) {
      return false;
    }
    // For admin and driver users, password is required when creating new users
    return !isEdit;
  };

  // Check if password fields should be shown
  const shouldShowPasswordFields = (): boolean => {
    // Hide password fields for client users
    if (formData.role === ROLES.CLIENT) {
      return false;
    }
    // Show for admin and driver users
    return true;
  };

  const validateForm = (): boolean => {
    const newErrors: UserFormErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('validation.required', { field: t('users.name') });
    }
    
    if (!formData.email) {
      newErrors.email = t('validation.required', { field: t('users.email') });
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('validation.email');
    }
    
    // Only validate password if it's required for this role
    if (isPasswordRequired() && !formData.password) {
      newErrors.password = t('validation.required', { field: t('users.password') });
    } else if (formData.password && formData.password.length < 6) {
      newErrors.password = t('validation.passwordLength', { min: 6 });
    }
    
    // Only validate password confirmation if password fields are shown
    if (shouldShowPasswordFields() && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('validation.passwordsDontMatch');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof UserFormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Remove confirmPassword from the data sent to API
      const { confirmPassword, ...userData } = formData;
      
      // For client users, remove password fields from payload
      if (formData.role === ROLES.CLIENT) {
        const { password, ...clientUserData } = userData;
        
        if (isEdit && id) {
          await usersApi.update(parseInt(id), clientUserData);
        } else {
          // Use the client-specific API endpoint for creating client users
          await usersApi.createClient(clientUserData);
        }
      } else {
        // For admin and driver users, use regular endpoints
        if (isEdit && id) {
          await usersApi.update(parseInt(id), userData);
        } else {
          await usersApi.create(userData);
        }
      }
      
      navigate('/users');
    } catch (err: any) {
      console.error('Error saving user:', err);
      setError(err.message || t('errors.saveUser'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3} maxWidth="md">
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {isEdit ? t('users.editTitle') : t('users.createTitle')}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            margin="normal"
            label={t('users.name')}
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            required
          />
          
          <TextField
            fullWidth
            margin="normal"
            label={t('users.email')}
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            required
            disabled={isEdit}
          />
          
          <FormControl fullWidth margin="normal" error={!!errors.role}>
            <InputLabel id="role-label">{t('users.role')}</InputLabel>
            <Select
              name="role"
              value={formData.role}
              onChange={handleChange}
              label={t('users.role')}
              required
            >
              <MenuItem value="">
                <em>{t('users.selectRole')}</em>
              </MenuItem>
              {roleOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(`users.roles.${option.label}`)}
                </MenuItem>
              ))}
            </Select>
            {errors.role && <FormHelperText>{errors.role}</FormHelperText>}
          </FormControl>
          
          {shouldShowPasswordFields() && (
            <>
              <TextField
                fullWidth
                label={t('users.password')}
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                error={!!errors.password}
                helperText={errors.password}
                required={isPasswordRequired()}
                margin="normal"
              />

              <TextField
                fullWidth
                label={t('users.confirmPassword')}
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                required={isPasswordRequired()}
                margin="normal"
              />
            </>
          )}

          {/* Show informational message for client users */}
          {formData.role === ROLES.CLIENT && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('users.clientPasswordInfo')}
            </Alert>
          )}
          <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
            <Button 
              type="button" 
              onClick={() => navigate('/users')}
              disabled={submitting}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={submitting}
            >
              {submitting ? t('common.saving') : t('common.saveChanges')}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}
